import { useMemo } from 'react';
import { type HistoryRow, type ReportHistory } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { buildComparisonColumns } from './comparison-columns';
import { buildComparisonRows } from './patient-trends.logic';
import { TrendChart } from './TrendChart';
import { trendSeries } from './result-trend.logic';

/** Every visit of one test as a table, then one chart per parameter. */
export function TrendComparison({ history }: { history: ReportHistory }) {
  const { rows, visits, changed_ranges: changedRanges } = history.history;

  const columns = useMemo(() => buildComparisonColumns(visits), [visits]);

  // The honest version of "one visit, nothing to compare": said once the server
  // has actually looked, rather than guessed from the patient payload — which
  // holds only this visit's reports and cannot know about the others.
  //
  // Below the memo, not above it: an early return before a hook changes the
  // hook order between renders.
  if (visits.length === 0) {
    return (
      <Card title={history.test_name}>
        <EmptyState
          title="Only one visit so far"
          body="Nothing to compare this against yet. Earlier results appear here once this patient has had the test before."
        />
      </Card>
    );
  }

  return (
    <>
      <Card title={history.test_name} subtitle={`${visits.length + 1} visits`} flush>
        <DataTable
          columns={columns}
          rows={buildComparisonRows(rows)}
          rowKey={(row) => row.parameter_id}
          caption={`${history.test_name} across visits`}
        />

        {changedRanges.length > 0 && (
          <p className="c-field__help c-history__note">
            * The reference range has changed since an earlier visit for {changedRanges.join(', ')}.
            Each result is shown against the range that applied at its own visit.
          </p>
        )}
      </Card>

      <TrendChartsCard rows={rows.filter((row) => !row.retired)} />
    </>
  );
}

function TrendChartsCard({ rows }: { rows: readonly HistoryRow[] }) {
  return (
    <Card title="Over time" subtitle="One chart per parameter with more than one numeric result">
      <div className="c-trend-grid">
        {rows.map((row) => {
          const points = trendSeries([row.current, ...row.cells]);
          if (points.length < 2) return null;

          return (
            <div key={row.parameter_id} className="c-trend-cell">
              <p className="c-detail-list__label">
                {row.name}
                {row.unit && <span className="c-muted"> · {row.unit}</span>}
              </p>
              <TrendChart
                name={row.name}
                unit={row.unit}
                range={row.normal_range}
                points={points}
              />
            </div>
          );
        })}
      </div>
    </Card>
  );
}
